import React, { useState, useContext } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { NotificationContext } from '../context/NotificationContext';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';

interface RequestResetModalProps {
  show: boolean;
  onHide: () => void;
  onShowLogin?: () => void;
}

const RequestResetModal: React.FC<RequestResetModalProps> = ({ show, onHide, onShowLogin }) => {
  const { t } = useTranslation();
  const { addNotification } = useContext(NotificationContext);
  const { csrfToken } = useContext(AppContext);
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!email) {
      setError(t('auth.enter_email_error'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('auth.invalid_email_error'));
      return;
    }

    setIsLoading(true);

    const formData = new URLSearchParams();
    formData.append('PasswordResetRequestForm[email]', email);

    try {
      const response = await fetch('/user/credentials/request-reset?json=1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': csrfToken || '',
        },
        body: formData.toString(),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        addNotification({ message: t('auth.reset_email_sent', { email }), level: 'success' });
      } else {
        setError(data.error || t('auth.error_occurred'));
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      setError(t('auth.error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('auth.reset_password')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <div className="text-center py-4">
            <Alert variant="success">
              <h5>{t('auth.check_email')}</h5>
              <p className="mb-0">{t('auth.reset_link_sent', { email })}</p>
            </Alert>
            <Button
              variant="primary"
              onClick={() => {
                handleClose();
                if (onShowLogin) {
                  onShowLogin();
                }
              }}
              className="mt-3"
            >
              {t('auth.back_to_login')}
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} className="auth-form">
            {error && <Alert variant="danger">{error}</Alert>}
            
            <p className="text-muted mb-4">
              {t('auth.reset_instructions')}
            </p>
            
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Floating>
                    <Form.Control
                      type="email"
                      id="email"
                      placeholder={t('auth.email_address')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      autoFocus
                    />
                    <label htmlFor="email">{t('auth.email_address')}</label>
                  </Form.Floating>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('auth.sending')}
                    </>
                  ) : (
                    t('auth.send_reset_link')
                  )}
                </Button>
              </Col>
            </Row>

            {onShowLogin && (
              <Row className="mt-3">
                <Col xs={12} className="text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                      onShowLogin();
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    {t('auth.back_to_login_link')}
                  </a>
                </Col>
              </Row>
            )}
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RequestResetModal;